import { create } from "zustand"
import type { Country, Language, SkillSpecialty, Team } from "@/types/__generated__/union-types"

interface IShiftPlanFiltersState {
  agents: string[]
  shiftTypes: string[]
  languages: Language[]
  specialties: SkillSpecialty[]
  teams: Team[]
  licenses: Country[]
  qualifications: string[]
}

interface ShiftPlanAgent {
  readonly shifts: ReadonlyArray<{ readonly shiftType: string }>
  readonly qualifications: ReadonlyArray<string>
}

interface ShiftPlanFiltersActions {
  setSelectedDate: (date: Date) => void
  setAgents: (agents: string[]) => void
  setShiftTypes: (shiftTypes: string[]) => void
  setLanguages: (languages: Language[]) => void
  setSpecialties: (specialties: SkillSpecialty[]) => void
  setTeams: (teams: Team[]) => void
  setLicenses: (licenses: Country[]) => void
  setQualifications: (qualifications: string[]) => void
  updateAvailableOptions: (agents: ReadonlyArray<ShiftPlanAgent>) => void
  resetFilters: (isKeepDate?: boolean) => void
}

interface ShiftPlanStore {
  selectedDate: Date
  filters: IShiftPlanFiltersState
  availableShiftTypes: string[]
  availableQualifications: string[]
  isLoading: boolean
  actions: ShiftPlanFiltersActions
}

const initialFilters: IShiftPlanFiltersState = {
  agents: [],
  shiftTypes: [],
  languages: [],
  specialties: [],
  teams: [],
  licenses: [],
  qualifications: [],
}

export const hasActiveFilters = (filters: IShiftPlanFiltersState): boolean =>
  filters.agents.length > 0 ||
  filters.shiftTypes.length > 0 ||
  filters.languages.length > 0 ||
  filters.specialties.length > 0 ||
  filters.teams.length > 0 ||
  filters.licenses.length > 0 ||
  filters.qualifications.length > 0

export const useShiftPlanFiltersStore = create<ShiftPlanStore>()((set, get) => ({
  filters: initialFilters,
  selectedDate: new Date(),
  availableShiftTypes: [],
  availableQualifications: [],
  isLoading: false,
  actions: {
    setSelectedDate: (date) => set({ selectedDate: date, filters: initialFilters, isLoading: true }),
    setAgents: (agents) => set((state) => ({ filters: { ...state.filters, agents } })),
    setShiftTypes: (shiftTypes) => set((state) => ({ filters: { ...state.filters, shiftTypes } })),
    setLanguages: (languages) => set((state) => ({ filters: { ...state.filters, languages } })),
    setSpecialties: (specialties) => set((state) => ({ filters: { ...state.filters, specialties } })),
    setTeams: (teams) => set((state) => ({ filters: { ...state.filters, teams } })),
    setLicenses: (licenses) => set((state) => ({ filters: { ...state.filters, licenses } })),
    setQualifications: (qualifications) => set((state) => ({ filters: { ...state.filters, qualifications } })),
    updateAvailableOptions: (agents) => {
      if (hasActiveFilters(get().filters)) return

      const shiftTypes = new Set<string>()
      const qualifications = new Set<string>()
      for (const agent of agents) {
        for (const shift of agent.shifts) {
          shiftTypes.add(shift.shiftType)
        }
        for (const q of agent.qualifications) {
          qualifications.add(q)
        }
      }
      set({
        availableShiftTypes: [...shiftTypes],
        availableQualifications: [...qualifications].sort(),
        isLoading: false,
      })
    },
    resetFilters: (isKeepDate) =>
      set({
        filters: { ...initialFilters },
        selectedDate: isKeepDate ? get().selectedDate : new Date(),
        isLoading: true,
      }),
  },
}))
